import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { eppAdditionalOptions, eppOptions } from "./PumpsFormOptions";
import { useSelection } from "../../../../contexts/SelectionContext";
import { useAuth } from "../../../../contexts/AuthContext";
import {
  requestSeries,
  filterSeries,
  getPumpData,
  getPumpHash,
  findOptionsByHash,
  getDevMode,
} from "../../../../api/aquastrong";
import { selectionBase } from "../../../../utils/routes";
import {
  useLocation,
  useNavigate,
  useParams,
  matchPath,
} from "react-router-dom";
import SelectionForm from "../../../SelectionUI/SelectionForm";
import SubmitSelection from "../../../SelectionUI/SelectionSubmit";
import SolutionTabs from "../../../SelectionUI/SolutionTabs";
import SelectionErrorHandler from "../../../../utils/selectionErrorHandler";
import SeriesList from "./SeriesList";
import useMobile from "../../../../hooks/useMobile";
import SeriesImagesList from "./SeriesImagesList";
import SeriesSelectedOption from "./SeriesSelectedOption";
import downloadIcon from "../../../../images/icons/download.svg";
import loaderIcon from "../../../../images/icons/loader.svg";

const PumpsSelection = () => {
  const {
    handleInputChange,
    setInputErrors,
    inputErrors,
    setLoading,
    errorMessage,
    setErrorMessage,
    setSolutionResults,
    loading,
    checkEmptyField,
    setComponentData,
    setOptions,
    tabs,
    setTabs,
    basicData,
    currentSelectedPump,
    setCurrentSelectedPump,
    currentActiveTab,
    setCurrentActiveTab,
  } = useSelection();

  const { isAuth } = useAuth();

  const isMobile = useMobile(812);

  const [isPageReady, setIsPageReady] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [currentRangeList, setCurrentRangeList] = useState(undefined);
  const [nextTabToActivate, setNextTabToActivate] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false); // Флаг, что была выполнена фильтрация
  const [formOptions, setFormOptions] = useState([eppOptions]);
  const [loadingNewPump, setLoadingNewPump] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isLoadingGraphs, setIsLoadingGraphs] = useState(false);
  const [hasNewGraphsData, setHasNewGraphsData] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [pCurveName, setPcurveName] = useState("P1");
  const [seriesName, setSeriesName] = useState(null);
  const [currentActiveImageTab, setCurrentActiveImageTab] =
    useState("Характеристики");

  const [data, setData] = useState(basicData);

  const parseOptionsOutputData = (options) => {
    const output = options?.response?.output;
    if (!output) return undefined;
    try {
      const parsed = typeof output === "string" ? JSON.parse(output) : output;
      return parsed?.data;
    } catch (error) {
      // If output isn't valid JSON, skip the parsed data
      return undefined;
    }
  };

  const dataRef = useRef(data);
  const updateTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const prevDataRef = useRef({});
  const inputErrorsRef = useRef(inputErrors);
  const initialRangeListRef = useRef(null);
  const initialLoadRef = useRef(true);

  const navigate = useNavigate();
  const { pumpId } = useParams();
  const location = useLocation();

  const currentPath = useLocation().pathname;

  // Получение номера текущей серии из URL
  const currentPumpSeries = useMemo(() => {
    const segments = currentPath.split("/").filter(Boolean);
    return segments.length >= 2 ? segments[2] : null;
  }, [currentPath]);

  const emptyFormError = "Ошибка фильтрации насосов: Данные формы пусты";

  /**
   * Проверяет наличие ошибки в текущем URL и, если необходимо,
   * сбрасывает сообщение об ошибке и результаты решения, а также
   * перенаправляет пользователя на корректный URL для выбранной серии насосов.
   */
  const checkCurrentURLerror = () => {
    if (
      errorMessage &&
      tabs[currentActiveTab].type === "default" &&
      (currentPath !== `${selectionBase()}/${currentPumpSeries}` ||
        errorMessage?.startsWith("Неверный URL"))
    ) {
      setErrorMessage(undefined);
      setSolutionResults(undefined);
      let newUrl = `${selectionBase()}/${currentPumpSeries}`;
      navigate(newUrl, { replace: true });
    }
  };

  /**
   * Подготавливает данные формы для отправки на сервер.
   * Преобразует значения полей с типом "number" в числовой формат,
   * остальные значения оставляет без изменений.
   *
   * @param {object} data - Объект с исходными данными формы
   * @returns {object} processedData - Обработанный объект данных
   */
  const prepareData = (data) => {
    const processedData = {};

    const allOptionsArray = [
      ...eppAdditionalOptions,
      ...eppOptions.selectionOptions,
    ];

    for (const [key, value] of Object.entries(data)) {
      // Концентрация и температура передаются только для гликоля
      if ((key === "Conc" || key === "Temp") && data.Liq !== 3) continue;

      const fieldType = allOptionsArray.find(
        (opt) => opt.name === key
      )?.fieldType;

      if (fieldType === "number" && value !== null && value !== "") {
        processedData[key] = Number(value);
      } else {
        processedData[key] = value;
      }
    }

    return processedData;
  };

  /**
   * Проверяет, являются ли значения данных формы "по умолчанию".
   * Возвращает true, если все параметры Q, H, H0 равны 0, а также:
   * - если присутствует pcnt, он должен быть равен 1
   * - если присутствует BoxType, он должен быть равен 2
   * В противном случае возвращает false.
   *
   * @param {object} data - Данные формы для проверки
   * @returns {boolean} - true, если данные соответствуют значениям по умолчанию, иначе false
   */
  const checkDataForDefault = (data) => {
    if (!data || data === null) return;

    if ("pcnt" in data && "BoxType" in data) {
      if (
        data.Q === 0 &&
        data.H === 0 &&
        data.H0 === 0 &&
        data.pcnt === 1 &&
        data.BoxType === 2
      )
        return true;
      else return false;
    } else if ("pcnt" in data) {
      if (data.Q === 0 && data.H === 0 && data.H0 === 0 && data.pcnt === 1)
        return true;
      else return false;
    } else if ("BoxType" in data) {
      if (data.Q === 0 && data.H === 0 && data.H0 === 0 && data.BoxType === 2)
        return true;
      else return false;
    } else {
      if (data.Q === 0 && data.H === 0 && data.H0 === 0) return true;
      else return false;
    }
  };

  /**
   * Получает полный набор данных насоса, включая его графики,
   * на основе ID насоса и данных формы
   *
   * @param {string} id - ID насоса
   * @param {object} data - Данные формы
   * @returns {Promise<{error: (boolean|string), foundPump: object, options: object}>}
   * Промис, который возвращает объект, содержащий:
   * - `error`: `false` при успехе, или строку с сообщением об ошибке
   * - `foundPump`: Объект с данными найденного насоса
   * - `options`: Объект с графиками и файлами
   */
  const getPump = async (id, data) => {
    try {
      const isDataNull = checkDataForDefault(data);
      if (!isDataNull) {
        data = prepareData(data);
      }

      const [foundPumpResponse, hashResponse] = isDataNull
        ? await Promise.all([
            getPumpData(id),
            getPumpHash(id, null, data?.units),
          ])
        : await Promise.all([getPumpData(id, data), getPumpHash(id, data)]);

      // Проверяем, вернули ли запросы ошибки
      if (
        foundPumpResponse.error ||
        (hashResponse.error && !hashResponse.hash)
      ) {
        const errors = [];

        if (foundPumpResponse.error) {
          errors.push(foundPumpResponse.error);
        }

        if (foundPumpResponse.message) {
          errors.push(foundPumpResponse.message);
        }

        if (hashResponse.error && !hashResponse.hash) {
          errors.push(hashResponse.error);
        }

        const error =
          errors.length > 0
            ? errors.join("; ")
            : "Произошла непредвиденная ошибка";

        throw new Error(error);
      }

      const foundPump = foundPumpResponse;
      const hash = hashResponse;

      if (hash.hash === undefined) {
        const error = hash.error
          ? hash.error
          : "Произошла непредвиденная ошибка";
        throw new Error(error);
      }

      // Начинаем поллинг графиков
      setIsLoadingGraphs(true);
      setHasNewGraphsData(false);

      const options = await findOptionsByHash(hash.hash);
      // Проверяем наличие файлов - если есть файлы, считаем успехом
      if (!options || !options.files || options.files === null) {
        // Нет файлов - проверяем на ошибки
        if (options.error) {
          setIsLoadingGraphs(false);
          setHasNewGraphsData(false);
          throw new Error(options.message);
        }

        let jointErrors = "unknown";
        if (options.error !== null) {
          // Если есть violations с непустыми полями - используем violations, иначе error
          const hasViolations =
            options.violations &&
            typeof options.violations === "object" &&
            Object.keys(options.violations).length > 0;

          jointErrors = SelectionErrorHandler(
            hasViolations ? options.violations : options.error,
            options.id
          );
        }
        setIsLoadingGraphs(false);
        setHasNewGraphsData(false);
        throw new Error(jointErrors);
      }

      // Получили файлы (независимо от наличия ошибки) - считаем успехом
      setHasNewGraphsData(true);
      setIsLoadingGraphs(false);
      setErrorMessage(undefined);

      return { error: false, foundPump, options };
    } catch (error) {
      const errorMessage =
        error.message || "Не удалось получить новые данные для насоса";
      setErrorMessage(errorMessage);
      setSolutionResults(null);
      setLoadingNewPump(false);
      setIsLoadingGraphs(false); // Сбрасываем состояние загрузки графиков при ошибке
      setHasNewGraphsData(false);
      return { error: true, message: errorMessage };
    }
  };

  /**
   * Открывает новую вкладку нсоса используя полученные данные формы.
   * Корректирует данные формы, если она пустая, под минимальные ограничения
   * для отправки запроса и сохраняет состояние текущей вкладки
   *
   * @param {string} id - ID насоса
   * @param {object} data - Данные формы
   * @returns {Promise<void>}
   */
  const openPumpHandler = async (id, data, fromResults = false) => {
    try {
      const dataToPrepare = data;
      data = prepareData(dataToPrepare);

      checkCurrentURLerror();

      // При гликоле концентрация и температура обязательны
      const fieldsToCheck = ["Q", "H", "H0"];
      if (data.Liq === 3) fieldsToCheck.push("Conc", "Temp");

      // Проверяем, все ли из этих полей равны null
      const requiredFieldsAreNull = fieldsToCheck.every(
        (field) => data[field] === null
      );

      // Если все обязательные поля пустые - открываем вкладку с минимальными значениями,
      // если некоторые обязательные поля заполнены, а некоторые нет - выводим ошибку,
      // если все поля заполены - открываем вкладку по текущим данным формы
      if (!requiredFieldsAreNull) {
        const allFeildsNotNull = checkData(data);

        if (!allFeildsNotNull) {
          // Собираем ошибки только с уникальными inputId
          const currentErrorsMap = {};
          fieldsToCheck.forEach((field) => {
            if (data[field] === null && !currentErrorsMap[field]) {
              currentErrorsMap[field] = {
                inputId: field,
                message: "Поле не должно быть пустым",
              };
            }
          });
          const currentErrors = Object.values(currentErrorsMap);

          setTabs((prev) => ({
            ...prev,
            Default: {
              ...prev["Default"],
              error: {
                ...prev["Default"].error,
                emptyRule: currentErrors,
                valueRule: inputErrorsRef.current.valueRule,
              },
            },
          }));

          return;
        }
      }

      setLoadingNewPump(true);

      const formData = {};

      // Корректируем текущие значения формы под минимальные ограничения
      // для открытия новой вкладки
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          if (key === "Q" || key === "H") {
            formData[key] = data[key] === null ? 0 : data[key];
          } else if (key === "H0") {
            formData[key] = data[key] === null ? 0 : data[key];
          } else {
            formData[key] = data[key];
          }
        }
      }

      const { error, foundPump, options } = await getPump(id, formData);

      if (error) {
        setLoadingNewPump(false);
        setIsWaitingForResponse(false);
        return;
      }

      // Проверяем, что foundPump является объектом, а не строкой разбитой на символы
      let validFoundPump = foundPump;
      if (
        foundPump &&
        typeof foundPump === "object" &&
        !Array.isArray(foundPump)
      ) {
        // Проверяем, является ли это объектом с числовыми ключами (разбитая строка)
        const keys = Object.keys(foundPump);
        const hasNumericKeys =
          keys.length > 0 && keys.every((key) => /^\d+$/.test(key));

        if (hasNumericKeys && keys.length > 10) {
          // Это похоже на разбитую строку, пытаемся восстановить
          try {
            const reconstructedString = keys
              .sort((a, b) => parseInt(a) - parseInt(b))
              .map((key) => foundPump[key])
              .join("");
            validFoundPump = JSON.parse(reconstructedString);
          } catch (e) {
            // console.error("Failed to reconstruct foundPump:", e);
            // Если не удалось восстановить, используем исходный объект
          }
        }
      }

      const pumpWithFiles = {
        ...validFoundPump,
        files: options.files,
        optionsOutputData: parseOptionsOutputData(options),
      };

      setTabs(() => ({
        Default: {
          // Запоминаем текущие данные дефолтной вкладки
          type: "default",
          series: currentPumpSeries,
          formData: fromResults
            ? tabs["Default"].formData // Если метод был вызыван из таблицы во кладке Результаты подбора, тогда не обновляем дефолтную вкладку
            : dataRef.current,
          error: fromResults
            ? tabs["Default"].error
            : {
                request: errorMessage?.startsWith("Неверный URL")
                  ? undefined
                  : errorMessage,
                emptyRule: inputErrorsRef.current.emptyRule,
                valueRule: inputErrorsRef.current.valueRule,
              },
        },
        Pump: {
          // Добавляем новую вкладку с нужными данными
          type: "pump",
          data: pumpWithFiles,
          formData: formData, // Скорректированные значения формы
          error: {
            request: undefined,
            emptyRule: [],
            valueRule: undefined,
          },
        },
      }));

      prevDataRef.current = formData;

      setData(formData);

      setCurrentActiveTab("Pump");
      setNextTabToActivate("Pump");

      // Создаем queryParams
      const queryParams = new URLSearchParams();
      if (prevDataRef.current) {
        for (const [paramKey, value] of Object.entries(prevDataRef.current)) {
          if (value !== null && value !== undefined) {
            // Conc/Temp пишутся в URL только для гликоля
            if (
              (paramKey === "Conc" || paramKey === "Temp") &&
              prevDataRef.current.Liq !== 3
            )
              continue;
            queryParams.append(paramKey, value);
          }
        }
      }
      let newUrl = `${selectionBase()}/${currentPumpSeries}`;
      // Добавляем id насоса и queryParams в URL
      newUrl += `/${pumpWithFiles.id}?${queryParams.toString()}`;
      navigate(newUrl, { replace: true });

      setTabs((prev) => ({
        ...prev,
        Pump: {
          ...prev["Pump"],
          url: newUrl,
        },
      }));

      setErrorMessage(undefined);
      setInputErrors({
        emptyRule: [],
        valueRule: undefined,
      });
      setLoadingNewPump(false);
      setIsWaitingForResponse(false);

      // Вызываем триггер для скрола
      setShouldScroll(true);
    } catch (error) {
      // console.error("Error loading pump data: ", error);
      setSolutionResults(null);
      setErrorMessage("Ошибка при загрузке данных насоса");
      setLoadingNewPump(false);
      setIsWaitingForResponse(false);
    }
  };

  /**
   * Обрабатывает загрузку изображения
   *
   * @param {string} name - Имя загруженного изображения ('hqc', 'powerc', 'photo')
   */
  const handleImageLoaded = useCallback((name) => {
    // Функция для совместимости, но больше не используется
  }, []);

  /**
   * Устанавливает выбранный насос по его названию, извлекая данные из кэша вкладок.
   * Если дефолтная вкладка, то текущий выбранный насос сбрасывается.
   *
   * @param {string} name - Ключ (название насоса) вкладки
   */
  const setPumpByName = useCallback(
    (name) => {
      if (tabs[name]?.type === "pump") {
        setCurrentSelectedPump(tabs["Pump"].data);
      } else {
        setCurrentSelectedPump(undefined);
      }
    },
    [tabs, setCurrentSelectedPump]
  );

  /**
   * Проверяет, что все значения в объекте данных не равны null.
   * Если объект данных не передан или пуст, используется текущее значение dataRef.current.
   *
   * @param {object} data - Объект с данными для проверки.
   * @returns {boolean} - true, если все значения не равны null, иначе false.
   */
  const checkData = (data) => {
    if (!data || Object.keys(data).length === 0) {
      data = dataRef.current;
    }
    return Object.values(data).every((value) => value !== null);
  };

  /**
   * Обновить данные выбранного насоса из формы
   * @param {object} currentData - Текущие значения формы
   * @returns {Promise<void>}
   */
  const updatePump = async (currentData) => {
    const newAbortController = new AbortController();

    const dataToPrepare = currentData;

    currentData = prepareData(dataToPrepare);

    try {
      const allValuesNotNull = checkData();
      // Только если значения формы действительно поменялись
      const isDataChanged = !Object.entries(currentData).every(
        ([key, val]) => prevDataRef.current[key] === val
      );

      if (!allValuesNotNull || !isDataChanged) {
        setLoadingNewPump(false);
        setIsWaitingForResponse(false);
        return;
      }

      setLoadingNewPump(true);

      setErrorMessage(undefined);
      setInputErrors({
        emptyRule: [],
        valueRule: undefined,
      });

      // Перед отправкой нового запроса отменяем предыдущий,
      // если он существует
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = newAbortController;

      const { error, foundPump, options } = await getPump(
        currentSelectedPump.id,
        currentData
      );

      // Запоминаем использовавшиеся данные формы
      prevDataRef.current = currentData;

      // Если запрос был отменен
      if (newAbortController.signal.aborted) {
        setLoadingNewPump(false);
        setIsWaitingForResponse(false);
        setIsLoadingGraphs(false);
        setHasNewGraphsData(false);
        return;
      }

      // Если вернулась ошибка
      if (error) {
        setTabs((prev) => ({
          ...prev,
          Pump: {
            ...prev["Pump"],
            formData: currentData, // Сохраняем текущие значения формы
            error: {
              ...prev["Pump"].error,
              request: error,
            }, // Сохраняем текущую ошибку
          },
        }));
        setLoadingNewPump(false);
        setIsWaitingForResponse(false);
        setIsLoadingGraphs(false);
        setHasNewGraphsData(false);
        return;
      }

      const pdfName = Object.keys(options.files).filter((item) =>
        item.endsWith(".pdf")
      )[0];

      // Проверяем, что foundPump является объектом, а не строкой разбитой на символы
      let validFoundPump = foundPump;
      if (
        foundPump &&
        typeof foundPump === "object" &&
        !Array.isArray(foundPump)
      ) {
        // Проверяем, является ли это объектом с числовыми ключами (разбитая строка)
        const keys = Object.keys(foundPump);
        const hasNumericKeys =
          keys.length > 0 && keys.every((key) => /^\d+$/.test(key));

        if (hasNumericKeys && keys.length > 10) {
          // Это похоже на разбитую строку, пытаемся восстановить
          try {
            const reconstructedString = keys
              .sort((a, b) => parseInt(a) - parseInt(b))
              .map((key) => foundPump[key])
              .join("");
            validFoundPump = JSON.parse(reconstructedString);
          } catch (e) {
            // console.error("Failed to reconstruct foundPump:", e);
            // Если не удалось восстановить, используем исходный объект
          }
        }
      }

      // Если ошибки нет, обновляем графики
      const pumpWithFiles = {
        ...validFoundPump,
        files: {
          ...currentSelectedPump.files,
          "HQc.png": options.files["HQc.png"],
          "Powerc.png": options.files["Powerc.png"],
          [pdfName]: options.files[pdfName],
        },
        optionsOutputData: parseOptionsOutputData(options),
      };

      // Обновляем текущий выбранный насос для рендера
      setCurrentSelectedPump(pumpWithFiles);

      // Сохраняем новые данные текущей вкладки
      setTabs((prev) => ({
        ...prev,
        Pump: {
          ...prev["Pump"],
          type: "pump",
          data: pumpWithFiles,
          formData: currentData,
          error: {
            ...prev["Pump"].error,
            request: undefined,
          }, // Сбрасывем ошибку
        },
      }));

      // Создаем queryParams
      const queryParams = new URLSearchParams();
      if (prevDataRef.current) {
        for (const [paramKey, value] of Object.entries(prevDataRef.current)) {
          if (value !== null && value !== undefined) {
            // Conc/Temp пишутся в URL только для гликоля
            if (
              (paramKey === "Conc" || paramKey === "Temp") &&
              prevDataRef.current.Liq !== 3
            )
              continue;
            queryParams.append(paramKey, value);
          }
        }
      }

      let newUrl = `${selectionBase()}/${currentPumpSeries}`;

      // Добавляем id насоса и queryParams в URL
      if (
        tabs[currentActiveTab].type === "pump" &&
        currentSelectedPump.id &&
        queryParams.toString()
      ) {
        // Если вкладка типа "pump", добавляем ID насоса
        newUrl += `/${currentSelectedPump.id}?${queryParams.toString()}`;
      }

      navigate(newUrl, { replace: true });

      setTabs((prev) => ({
        ...prev,
        Pump: {
          ...prev["Pump"],
          url: newUrl,
        },
      }));

      setLoadingNewPump(false);
      setIsWaitingForResponse(false);
      // Вызываем триггер для скрола
      setShouldScroll(true);
    } catch (error) {
      if (error.name === "AbortError") {
        // console.error("Fetch aborted");
        return;
      }
      // console.error("Error updating pump data: ", error);
      setErrorMessage("Ошибка при обновлении насоса");
      setSolutionResults(null);
      setLoadingNewPump(false);
      setIsWaitingForResponse(false);
      setIsLoadingGraphs(false);
      setHasNewGraphsData(false);
    } finally {
      // Сбрасываем аборт контроллер
      if (abortControllerRef.current === newAbortController) {
        abortControllerRef.current = null;
      }
    }
  };

  /**
   * Устанавливает значение указанного поля формы в 0,
   * обновляет данные формы и очищает ошибку "пустого" значения для этого поля
   * в текущей активной вкладке.
   *
   * @param {string} field - Имя поля, которое нужно обнулить
   * @returns {object} updatedData - Обновлённые данные формы
   */
  const updateFieldToZero = (field) => {
    const updatedData = { ...dataRef.current, [field]: 0 };

    dataRef.current = updatedData;

    setData(updatedData);

    const tabName = currentActiveTab === "Default" ? currentActiveTab : "Pump";

    setTabs((prev) => ({
      ...prev,
      [tabName]: {
        ...prev[tabName],
        formData: updatedData,
        error: {
          ...prev[tabName].error,
          emptyRule: prev[tabName].error.emptyRule?.filter(
            (item) => item.inputId !== field
          ),
        },
      },
    }));

    return updatedData;
  };

  /**
   * Обрабатывает инпутов, обновляет данные,
   * проверяет валидность введенных значений,
   * сохраняет ошибки валидации в состоянии текущей вкладки и инициирует обновление насоса
   * с задержкой для предотвращения избыточных запросов
   *
   * @param {string} field - Имя инпута
   * @param {*} value - Новое значение инпута
   * @param {string} [measureId] - ID поля измерения (если есть)
   * @param {*} [measureValue] - Значение поля измерения (если есть)
   */
  const onInputChangeHandler = (field, value, measureId, measureValue) => {
    if (isWaitingForResponse || loadingNewPump) return;

    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
      updateTimerRef.current = null;
    }

    handleInputChange(field, value, measureId, measureValue);

    checkCurrentURLerror();

    try {
      // Данные формы с полученными данными инпута
      const tempUpdatedData = {
        ...dataRef.current,
        [field]: value,
      };

      dataRef.current = tempUpdatedData;

      let updatedValueRule = undefined;

      if (
        tempUpdatedData.Q === 0 &&
        tempUpdatedData.H === 0 &&
        tempUpdatedData.H0 === 0
      ) {
      } else {
        if (
          tempUpdatedData.H !== null &&
          tempUpdatedData.H0 !== null &&
          tempUpdatedData.H <= tempUpdatedData.H0
        ) {
          updatedValueRule = {
            inputId: "H",
            message: "Требуемый напор должен быть больше статического напора",
          };
        }
      }

      const isEmptyError = checkEmptyField(field, value);

      const tabName =
        currentActiveTab === "Default" ? currentActiveTab : "Pump";

      if (!isEmptyError) {
        // Если поле пустое, добавляем его в массив ошибок emptyRule
        setTabs((prev) => {
          // Удаляем все объекты с таким же inputId, чтобы не было дублей
          const prevEmptyRule = prev[tabName].error.emptyRule || [];
          const filteredEmptyRule = prevEmptyRule.filter(
            (item) => item.inputId !== field
          );
          return {
            ...prev,
            [tabName]: {
              ...prev[tabName],
              formData: tempUpdatedData,
              error: {
                ...prev[tabName].error,
                valueRule: updatedValueRule,
                emptyRule: [
                  ...filteredEmptyRule,
                  {
                    inputId: field,
                    message: "Поле не должно быть пустым",
                  },
                ],
              },
            },
          };
        });
      } else {
        setTabs((prev) => ({
          ...prev,
          [tabName]: {
            ...prev[tabName],
            formData: tempUpdatedData,
            error: {
              ...prev[tabName].error,
              valueRule: updatedValueRule,
              emptyRule: prev[tabName].error.emptyRule?.filter(
                (item) => item.inputId !== field // Если в массиве все еще есть ошибка для данного поля, удаляем ее
              ),
            },
          },
        }));
      }

      // Если поле H и оно стало пустым (null или ""), но только если Q и H0 равны 0, тогда подставляем 0 и вызываем updatePump, иначе не вызываем updatePump
      if (
        field === "H" &&
        (value === null || value === "" || value === undefined)
      ) {
        // Проверяем значения Q и H0
        const Q = dataRef.current["Q"];
        const H0 = dataRef.current["H0"];
        if (
          (Q === 0 || Q === "0" || Q === null || Q === "" || Q === undefined) &&
          (H0 === 0 ||
            H0 === "0" ||
            H0 === null ||
            H0 === "" ||
            H0 === undefined)
        ) {
          // Разрешаем подставить 0 и вызвать updatePump (логика ниже)
        } else {
          return;
        }
      }

      if (tabs[currentActiveTab]?.type === "default" || updatedValueRule)
        return;

      // Устанавливаем текущий таймер на 1сек
      updateTimerRef.current = setTimeout(() => {
        if (isWaitingForResponse || loadingNewPump) return;

        let newData;
        if (
          field === "H" &&
          (dataRef.current[field] === null ||
            dataRef.current[field] === "" ||
            dataRef.current[field] === undefined)
        ) {
          // Проверяем значения Q и H0
          const Q = dataRef.current["Q"];
          const H0 = dataRef.current["H0"];
          if (
            (Q === 0 ||
              Q === "0" ||
              Q === null ||
              Q === "" ||
              Q === undefined) &&
            (H0 === 0 ||
              H0 === "0" ||
              H0 === null ||
              H0 === "" ||
              H0 === undefined)
          ) {
            // Только если Q и H0 равны 0, подставляем 0 в H
            newData = updateFieldToZero(field);
          } else {
            updateTimerRef.current = null;
            return;
          }
        } else {
          newData =
            dataRef.current[field] === null
              ? updateFieldToZero(field)
              : dataRef.current;
        }

        setIsWaitingForResponse(true);
        updatePump(newData);
        updateTimerRef.current = null;
      }, 1000);
    } catch (error) {
      // console.error("Error changing input: ", error);
    }
  };

  /**
   * Обрабатывает событие потери фокуса на поле ввода.
   * Если существует активный таймер обновления, отменяет его и
   * немедленно отправляет обновленные данные на сервер.
   *
   * @param {string} field - Имя поля ввода
   */
  const handleInputBlur = (field) => {
    if (updateTimerRef.current) {
      let newData =
        dataRef.current[field] === null
          ? updateFieldToZero(field)
          : dataRef.current;

      clearTimeout(updateTimerRef.current); // Отменяем текущий таймер
      updateTimerRef.current = null;

      setIsWaitingForResponse(true);

      updatePump(newData); // Моментально отправляем запрос

      updateTimerRef.current = null;
    }
  };

  /**
   * Сбрасывает форму в исходное состояние,
   * устанавливая значения полей по умолчанию
   */
  const handleResetForm = () => {
    setIsFiltered(false); // Сбрасываем флаг фильтрации при сбросе формы
    let optionalFields = [];

    if ("BoxType" in data) optionalFields.push("BoxType");
    if ("pcnt" in data) optionalFields.push("pcnt");

    // Установка дефолтных значений дополнительным полям
    const additionalData = {};

    if (optionalFields.includes("pcnt")) {
      additionalData.pcnt = 1;
    }
    if (optionalFields.includes("BoxType")) {
      additionalData.BoxType = 2;
    }

    // Жидкость по умолчанию - вода
    if (getDevMode()) additionalData.Liq = 0;

    const fullData = { ...basicData, ...additionalData };

    dataRef.current = fullData;

    setData(fullData);

    setTabs((prev) => ({
      ...prev,
      Default: {
        type: "default",
        series: currentPumpSeries,
        formData: fullData,
        error: {
          request: undefined,
          emptyRule: [],
          valueRule: undefined,
        },
      },
    }));

    setCurrentRangeList(initialRangeListRef.current);

    if (currentActiveTab !== "Default") {
      navigateBackHandler();
    } else {
      // Если уже на Default, просто скроллим
      setShouldScroll(true);
    }
  };

  /**
   * Обрабатывает событие изменения активной вкладки изображений.
   *
   * @param {string} key - Ключ вкладки
   */
  const changeImageTabHandler = (key) => {
    setCurrentActiveImageTab(key);
  };

  /**
   * Открывает PDF файл, связанный с выбранным насосом
   */
  const openPdf = () => {
    const pdfFileName = Object.keys(currentSelectedPump?.files).filter((item) =>
      item.endsWith(".pdf")
    )[0];

    const url = currentSelectedPump.files[pdfFileName];

    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Обрабатывает нажатие на кнопку "Назад" и перенаправляет пользователя
   * на страницу выбора серии насосов
   */
  const navigateBackHandler = () => {
    navigate(`${selectionBase()}/${currentPumpSeries}`);

    setCurrentSelectedPump(undefined);
    setCurrentActiveTab("Default");

    setShouldScroll(true);
  };

  /**
   * Обрабатывает отправку формы, получает отфильтрованный по
   * заданным параметрам список насосов
   *
   * @param {Event} e - Событие отправки формы
   * @returns {Promise<void>}
   */
  const onSubmit = async (e) => {
    e.preventDefault();

    checkCurrentURLerror();

    if (inputErrors.valueRule || inputErrors.emptyRule.length > 0) {
      setShouldScroll(true);
      return;
    }

    try {
      if (tabs[currentActiveTab]?.type === "default") {
        setErrorMessage(undefined);
        setInputErrors({
          emptyRule: [],
          valueRule: undefined,
        });

        setSolutionResults(undefined);

        const newData = prepareData(data);

        const isFormEmpty = checkDataForDefault(newData);

        if (isFormEmpty) {
          setErrorMessage(emptyFormError);
          setShouldScroll(true);
          return;
        }

        setIsWaitingForResponse(true);

        // Получаем отфильтрованный список насосов серии удовлетворяющих заданным параметрам
        const result = await filterSeries(currentPumpSeries, newData);

        if (!Array.isArray(result)) {
          setIsWaitingForResponse(false);

          if ("error" in result && result.error && "message" in result)
            setErrorMessage(result.message);
          else if ("error" in result) setErrorMessage(result.error);
          else setErrorMessage("Произошла непредвиденная ошибка");

          setShouldScroll(true);
          setSolutionResults(null);
          return;
        }

        setIsFiltered(true); // Устанавливаем флаг, что была выполнена фильтрация
        setCurrentRangeList(result);

        if (result.length > 0 && result[0]?.id)
          openPumpHandler(result[0].id, data);
        // Если открываем насос, не останавливаем лоадер
        else if (result.length === 0) {
          setIsWaitingForResponse(false); // Если не открываем насос, останавливаем лоадер
          setShouldScroll(true);
        }
      }
    } catch (error) {
      // console.error("Error submiting form: ", error);
      setErrorMessage("Ошибка во время обработки результатов запроса.");
      setSolutionResults(null);
      setIsWaitingForResponse(false);
      setShouldScroll(true);
    }
  };

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    inputErrorsRef.current = inputErrors;
  }, [inputErrors]);

  // Останавливаем лоадер, если появилось сообщение об ошибке
  useEffect(() => {
    if (errorMessage?.length > 0) setLoadingNewPump(false);
  }, [errorMessage]);

  useEffect(() => {
    if (currentRangeList?.length === 0) setSolutionResults([]);
  }, [currentRangeList, setSolutionResults]);

  useEffect(() => {
    if (!currentSelectedPump || !currentRangeList) return;

    const isPumpVisible = currentRangeList.some(
      (item) => item.id === currentSelectedPump.id
    );

    if (isPumpVisible) return;

    const message =
      "Вы вышли из аккаунта. Этот насос доступен только авторизованным пользователям.";

    setTabs((prev) => ({
      Default: {
        ...prev.Default,
        error: {
          ...prev.Default.error,
          request: message,
        },
      },
    }));
    setCurrentSelectedPump(undefined);
    setCurrentActiveTab("Default");

    if (currentPumpSeries) {
      navigate(`${selectionBase()}/${currentPumpSeries}`, { replace: true });
    }
  }, [
    currentSelectedPump,
    currentRangeList,
    currentPumpSeries,
    navigate,
    setCurrentActiveTab,
    setCurrentSelectedPump,
    setTabs,
  ]);

  useEffect(() => {
    if (isAuth || !currentSelectedPump || !currentPumpSeries) return;
    // Не выполняем валидацию доступа, если была выполнена фильтрация
    // чтобы не перезаписать результаты фильтрации
    if (isFiltered) return;

    let isCancelled = false;

    const validateAccess = async () => {
      try {
        // Используем endpoint "series" вместо "filter" для получения списка насосов
        // так как "filter" предназначен только для POST запросов с данными формы
        const result = await requestSeries("series", currentPumpSeries);
        if (!Array.isArray(result)) return;

        const isVisible = result.some(
          (item) => item.id === currentSelectedPump.id
        );

        if (isVisible || isCancelled) return;
      } catch (error) {
        if (isCancelled) return;
      }

      const message =
        "Вы вышли из аккаунта. Этот насос доступен только авторизованным пользователям.";

      setTabs((prev) => ({
        Default: {
          ...prev.Default,
          error: {
            ...prev.Default.error,
            request: message,
          },
        },
      }));
      setCurrentSelectedPump(undefined);
      setCurrentActiveTab("Default");
      navigate(`${selectionBase()}/${currentPumpSeries}`, { replace: true });
    };

    validateAccess();

    return () => {
      isCancelled = true;
    };
  }, [
    currentPumpSeries,
    currentSelectedPump,
    isAuth,
    navigate,
    setCurrentActiveTab,
    setCurrentSelectedPump,
    setTabs,
    isFiltered,
  ]);

  // Устанавливаем новый solutionResults для вывода информации о насосе
  useEffect(() => {
    setLoadingNewPump(false);
    setSolutionResults(currentSelectedPump);
  }, [currentSelectedPump, setSolutionResults]);

  // При выборе в списке насоса, закидываем его в новую активную вкладку
  useEffect(() => {
    if (!currentSelectedPump) return;
    setSolutionResults(currentSelectedPump);
  }, [currentSelectedPump, setSolutionResults]);

  /**
   * Реагирует на изменение активной вкладки или состояния вкладок.
   * В зависимости от типа текущей вкладки устанавливает состояние загрузки.
   * Также подтягивает ошибки для текущей вкладки, обновляет сообщения об ошибках и ошибки валидации,
   * а при наличии ошибки запроса сбрасывает результаты решения.
   * В конце всегда сбрасывает индикатор загрузки нового насоса.
   */
  useEffect(() => {
    if (currentActiveTab in tabs) {
      const tabType = tabs[currentActiveTab]?.type;
      if (tabType === "default") setLoading(false);
      else setLoading(true);
    } else return;

    // Подтягиваем ошибки текущей вкладки
    if (tabs[currentActiveTab]) {
      setErrorMessage(tabs[currentActiveTab].error.request);
      setInputErrors({
        emptyRule: tabs[currentActiveTab].error.emptyRule,
        valueRule: tabs[currentActiveTab].error.valueRule,
      });
      if (tabs[currentActiveTab].error.request) setSolutionResults(null);
    }

    setLoadingNewPump(false);
  }, [
    currentActiveTab,
    tabs,
    setErrorMessage,
    setInputErrors,
    setLoading,
    setSolutionResults,
  ]);

  /**
   * Реагирует на изменение текущей активной вкладки.
   * Если для активной вкладки существуют данные формы, устанавливает их в состояние data
   * и обновляет prevDataRef. В противном случае сбрасывает данные к базовым значениям.
   */
  useEffect(() => {
    if (!currentActiveTab) return;

    if (tabs[currentActiveTab]?.formData) {
      setData(tabs[currentActiveTab].formData);
      prevDataRef.current = tabs[currentActiveTab].formData;
    } else {
      setData(basicData);
      prevDataRef.current = basicData;
    }
  }, [currentActiveTab]);

  /**
   * Реагирует на изменение nextTabToActivate.
   * Если nextTabToActivate не null, устанавливает текущую вкладку в состояние nextTabToActivate
   * и обновляет pumpByName.
   */
  useEffect(() => {
    if (nextTabToActivate === null) return;

    if (nextTabToActivate && Object.keys(tabs).includes(nextTabToActivate)) {
      setCurrentActiveTab(nextTabToActivate);
      setPumpByName(nextTabToActivate);
      setNextTabToActivate(null);
    }
  }, [nextTabToActivate, setPumpByName, tabs, setCurrentActiveTab]);

  // Если осталась только одна дефолтная вкладка, скрываем компонент
  useEffect(() => {
    if (Object.keys(tabs).length === 1) {
      setCurrentSelectedPump(undefined);
    }
  }, [tabs, setCurrentSelectedPump]);

  useEffect(() => {
    setComponentData(setData);
  }, [setComponentData]);

  /**
   * Сбрасывает флаг фильтрации при смене серии насосов
   */
  useEffect(() => {
    // Проверяем, есть ли параметры фильтрации в URL
    // Если есть, не сбрасываем флаг, так как фильтрация будет выполнена из URL
    const queryParams = new URLSearchParams(location.search);
    const hasFilterParams = queryParams.has("Q") && queryParams.has("H") && queryParams.has("H0");
    
    // Сбрасываем флаг только если нет параметров фильтрации в URL
    if (!hasFilterParams) {
      setIsFiltered(false);
    }
  }, [currentPumpSeries, location.search]);

  /**
   * Реагирует на изменение currentPumpSeries.
   * Получает список всех насосов текущей серии и устанавливает его в состояние initialRangeListRef.
   * Также устанавливает состояние isPageReady в true, если список насосов не пуст.
   */
  useEffect(() => {
    if (!currentPumpSeries) return;
    
    // Проверяем, есть ли параметры фильтрации в URL
    // Если есть, не загружаем все насосы, так как будет выполнена фильтрация из URL
    const queryParams = new URLSearchParams(location.search);
    const hasFilterParams = queryParams.has("Q") && queryParams.has("H") && queryParams.has("H0");
    
    // НЕ загружаем список насосов, если уже была выполнена фильтрация
    // чтобы не перезаписать результаты POST запроса (топ-7 насосов)
    if (isFiltered || hasFilterParams) {
      setIsPageReady(true);
      return;
    }

    // Получение списка всех насосов текущей серии
    const getSeriesList = async (id) => {
      try {
        if (!id) return;

        const result = await requestSeries("filter", id);

        if (!Array.isArray(result)) return;

        initialRangeListRef.current = result;
        setCurrentRangeList(result);
        setIsPageReady(true);
        setShouldScroll(true);
      } catch (error) {
        // console.error("Error getting series list:", error);
      }
    };

    getSeriesList(currentPumpSeries);
  }, [currentPumpSeries, isAuth, location.key, isFiltered, location.search]);

  /**
   * Реагирует на изменение formOptions.
   * Получает массив всех опций формы и устанавливает его в состояние options.
   */
  useEffect(() => {
    if (!formOptions) return;
    const optionsArray = formOptions.flatMap((item) => item.selectionOptions);
    setOptions(optionsArray);
  }, [formOptions, setOptions]);

  // Получение параметров для серии и парсинг URL
  useEffect(() => {
    if (!currentPumpSeries) return;

    // Эффект выполняется только при первой загрузке страницы
    if (initialLoadRef.current) {
      initialLoadRef.current = false;

      const setInitialData = async () => {
        let optionalFields = [];

        // Получение списка параметров насосов для серии
        const result = await requestSeries("series", currentPumpSeries);

        if (result.P_CurveName !== pCurveName)
          setPcurveName(result.P_CurveName);

        if (result.has_control) optionalFields.push("BoxType");
        if (result.has_count) optionalFields.push("pcnt");

        // Поля гликоля доступны только в dev-версии калькулятора
        if (getDevMode()) optionalFields.push("Liq", "Conc", "Temp");

        const filteredOptions = eppAdditionalOptions.filter((opt) =>
          optionalFields.includes(opt.name)
        );

        // Обновление списка полей для отображения в форме
        setFormOptions((prev) => [
          {
            ...prev[0],
            selectionOptions: [...prev[0].selectionOptions, ...filteredOptions],
          },
        ]);

        // Установка дефолтных значений дополнительным полям
        const additionalData = {};

        if (optionalFields.includes("pcnt")) {
          additionalData.pcnt = 1;
        }
        if (optionalFields.includes("BoxType")) {
          additionalData.BoxType = 2;
        }

        // Жидкость по умолчанию - вода
        if (getDevMode()) additionalData.Liq = 0;

        const fullData = { ...basicData, ...additionalData };

        setData(fullData);

        // Далее проверка содержимого URL
        const queryParams = new URLSearchParams(location.search);
        const urlData = {};
        let hasUrlData = false;

        const isSelectionPage =
          currentPath === `${selectionBase()}/${currentPumpSeries}` ||
          currentPath === `${selectionBase()}/${currentPumpSeries}/`
            ? true
            : false;

        // Проверяем URL параметры только если ссылка на насос, а не на форму подбора
        if (!isSelectionPage) {
          // Проверяем есть ли query-параметры в URL и не пустые ли у них значения
          // Гликолевые параметры разрешены только в dev-версии калькулятора
          const glycolParams = getDevMode() ? ["Liq", "Conc", "Temp"] : [];

          for (const [key, value] of queryParams.entries()) {
            if (!fullData.hasOwnProperty(key) && !glycolParams.includes(key)) {
              setCurrentActiveTab("Default");
              setSolutionResults(null);
              setErrorMessage(
                `Неверный URL: параметр '${key}' не является допустимым параметром для этой серии насосов. `
              );
              return;
            }
            if (value === "") {
              setCurrentActiveTab("Default");
              setSolutionResults(null);
              setErrorMessage(
                `Неверный URL: значение параметра '${key}' не может быть пустым. `
              );
              return;
            }

            urlData[key] = isNaN(Number(value)) ? value : Number(value);
            hasUrlData = true;
          }
          
          // Если в URL есть параметры фильтрации, устанавливаем флаг фильтрации
          // чтобы предотвратить загрузку всех насосов
          if (hasUrlData && urlData.Q !== undefined && urlData.H !== undefined && urlData.H0 !== undefined) {
            const urlFormData = { ...fullData, ...urlData };
            const isFormEmpty = checkDataForDefault(urlFormData);
            if (!isFormEmpty) {
              setIsFiltered(true);
            }
          }

          // Если URL вида aquastrong/{pumpSeries}/{pumpId}/, т.е. с айди насоса,
          // но без данных формы, открываем насос с минимальными занчениями формы
          if (
            !hasUrlData &&
            (currentPath.endsWith(`${currentPumpSeries}/${pumpId}`) ||
              currentPath.endsWith(`${currentPumpSeries}/${pumpId}/`))
          ) {
            const initialPumpFormData = fullData;

            // Устанавливаем минимальные значения формы
            for (const key in data) {
              if (data.hasOwnProperty(key)) {
                if (key === "Q" || key === "H") {
                  initialPumpFormData[key] = data[key] === null ? 0 : data[key];
                } else if (key === "H0") {
                  initialPumpFormData[key] = data[key] === null ? 0 : data[key];
                } else {
                  initialPumpFormData[key] = data[key];
                }
              }
            }

            openPumpHandler(pumpId, initialPumpFormData);
            return;
          }

          // Проверяем, все ли обязательные поля содержатся в URL
          // Гликолевые параметры необязательны: без них подразумевается вода
          for (const field of Object.keys(fullData)) {
            if (glycolParams.includes(field)) continue;
            if (!(field in urlData) || urlData[field] === null) {
              setCurrentActiveTab("Default");
              setSolutionResults(null);
              setErrorMessage(`Неверный URL: отстуствует параметр '${field}'.`);
              return;
            }
          }

          // Проверяем типы данных
          for (const field of Object.keys(fullData)) {
            if (glycolParams.includes(field)) continue;
            if (
              (field !== "units" &&
                (!(
                  typeof urlData[field] === "number" &&
                  Number.isFinite(urlData[field])
                ) ||
                  (typeof urlData[field] === "number" &&
                    Number.isFinite(urlData[field]) &&
                    urlData[field] < 0))) ||
              (field === "units" &&
                urlData[field] !== "m" &&
                urlData[field] !== "l")
            ) {
              setCurrentActiveTab("Default");
              setSolutionResults(null);
              setErrorMessage(
                `Неверный URL: недопустимое значение параметра '${field}'.`
              );
              return;
            }
          }

          // Проверяем значения гликолевых параметров (только для dev)
          for (const field of glycolParams) {
            if (!(field in urlData)) continue;

            const isValid =
              field === "Liq"
                ? urlData.Liq === 0 || urlData.Liq === 3
                : field === "Conc"
                ? Number.isInteger(urlData.Conc) &&
                  urlData.Conc >= 25 &&
                  urlData.Conc <= 59
                : Number.isInteger(urlData.Temp) &&
                  urlData.Temp >= -25 &&
                  urlData.Temp <= 60;

            if (!isValid) {
              setCurrentActiveTab("Default");
              setSolutionResults(null);
              setErrorMessage(
                `Неверный URL: недопустимое значение параметра '${field}'.`
              );
              return;
            }
          }

          // Только если есть pumpId и все поля формы присутствуют в URL
          if (pumpId && hasUrlData) {
            const initialPumpFormData = { ...basicData, ...urlData };
            
            // Если в URL есть параметры фильтрации, выполняем фильтрацию
            // чтобы получить топ-7 насосов вместо всех насосов
            const hasFilterParams = urlData.Q !== undefined && urlData.H !== undefined && urlData.H0 !== undefined;
            const isFormEmpty = checkDataForDefault(initialPumpFormData);
            
            if (hasFilterParams && !isFormEmpty) {
              try {
                // Выполняем фильтрацию для получения топ-7 насосов
                const preparedData = prepareData(initialPumpFormData);
                const filterResult = await filterSeries(currentPumpSeries, preparedData);
                
                if (Array.isArray(filterResult)) {
                  // Устанавливаем флаг фильтрации и обновляем список насосов
                  setIsFiltered(true);
                  setCurrentRangeList(filterResult);
                }
              } catch (error) {
                // Если фильтрация не удалась, продолжаем без неё
              }
            }
            
            openPumpHandler(pumpId, initialPumpFormData);
          }
        }
      };

      setInitialData();
    }
  }, [
    pumpId,
    location.search,
    navigate,
    setLoading,
    basicData,
    currentPumpSeries,
    pCurveName,
    currentPath,
    data,
    setCurrentActiveTab,
    setErrorMessage,
    setSolutionResults,
  ]);

  /**
   * Реагирует на изменение currentPath.
   * Сбрасывает текущий выбранный насос и устанавливает активную вкладку в "Default".
   */
  useEffect(() => {
    if (!!matchPath(`${selectionBase()}/:seriesId/`, currentPath)) {
      setCurrentSelectedPump(undefined);
      setCurrentActiveTab("Default");
    }
  }, [currentPath, setCurrentActiveTab, setCurrentSelectedPump]);

  /**
   * Реагирует на изменение shouldScroll.
   * Если shouldScroll = true, прокручивает страницу к верху секции выбора насосов.
   * Логика скролла:
   * - Если активная вкладка "Pump" и isMobile, скролл к middle-column
   * - Если активная вкладка не "Pump" и isMobile, скролл к left-column
   * - В остальных случаях скролл к selection-top
   */
  useEffect(() => {
    if (shouldScroll) {
      let targetElement;

      const isPumpTabActive =
        currentActiveTab === "Pump" && tabs[currentActiveTab]?.type === "pump";

      if (isPumpTabActive && isMobile) {
        // Скролл к графикам на мобильной версии
        targetElement = document.querySelector(".middle-column");
      } else if (!isPumpTabActive && isMobile) {
        // Скролл к пустой форме на мобильной версии
        targetElement = document.querySelector(".left-column");
      } else {
        // Скролл к верху окна на десктопе
        targetElement = document.querySelector(".selection-top");
      }

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setShouldScroll(false);
    }
  }, [shouldScroll, currentActiveTab, tabs, isMobile]);

  return (
    <div className="aquastrong page-width_large">
      {(!isPageReady ||
        loadingNewPump ||
        isWaitingForResponse ||
        (isLoadingGraphs && !hasNewGraphsData)) && (
        <div className="loader">
          <img src={loaderIcon} alt="loader" />
        </div>
      )}

      {isPageReady ? (
        <div className="selections-wrapper">
          {/* Верхняя часть */}
          <div className="selection-top flex-row flex-between">
            {/* Левая колонка - Форма */}
            <div className="selection flex-column selection-block left-column border border-radius-l">
              <form className="form w-100">
                <h3>Общие параметры</h3>
                <SelectionForm
                  currentData={data}
                  options={formOptions}
                  changeData={onInputChangeHandler}
                  onMeasureChange={onInputChangeHandler}
                  onBlock={isWaitingForResponse ? true : false}
                  onBlur={(field, value, measureValue) =>
                    handleInputBlur(field, value, measureValue)
                  }
                />
                <div
                  className={`buttons-row ${
                    currentActiveTab === "Default" ? "flex-row" : "flex-column"
                  }`}
                >
                  <button
                    type="button"
                    className="btn-secondary size-m border-radius-s text-m"
                    onClick={() => handleResetForm()}
                  >
                    Сбросить
                  </button>

                  {currentActiveTab === "Default" ? (
                    <SubmitSelection onClick={onSubmit} isLoading={loading} />
                  ) : (
                    <button
                      type="button"
                      onClick={() => openPdf()}
                      className="download btn-primary flex-row"
                    >
                      Скачать PDF
                      <img alt="download" src={downloadIcon} />
                    </button>
                  )}
                </div>

                <div className="error-messages-container flex-column w-100">
                  {inputErrors.emptyRule.length > 0 && (
                    <span className="error-message text-xs">
                      {inputErrors.emptyRule[0].message}
                    </span>
                  )}
                  {inputErrors.valueRule && (
                    <span className="error-message text-xs">
                      {inputErrors.valueRule.message}
                    </span>
                  )}
                </div>
              </form>
            </div>

            {/* Средняя колонка - Графики */}
            <div className="flex-column selection-block middle-column border border-radius-l">
              {currentSelectedPump &&
                typeof errorMessage !== "string" &&
                !tabs[currentSelectedPump?.name]?.error?.request && (
                  <div className="images-info flex-column flex-between w-100">
                    <SeriesImagesList
                      currentSelectedPump={tabs[currentActiveTab]}
                      group={currentActiveImageTab}
                      onImageLoaded={handleImageLoaded}
                      deltaP={pCurveName}
                      isLoadingGraphs={isLoadingGraphs}
                      hasNewGraphsData={hasNewGraphsData}
                    />

                    <SolutionTabs
                      tabs={["Характеристики", "Размеры"]}
                      currentActiveName={currentActiveImageTab}
                      onSelectTab={(key, index) =>
                        changeImageTabHandler(key, index)
                      }
                    />
                  </div>
                )}

              {errorMessage &&
                typeof errorMessage === "string" &&
                !errorMessage.startsWith(emptyFormError) && (
                  <div className="default-message_container">
                    <span className="error-message text-xs">
                      {errorMessage}
                    </span>
                  </div>
                )}

              {!currentSelectedPump &&
                (errorMessage === undefined ||
                  (typeof errorMessage === "string" &&
                    errorMessage.startsWith(emptyFormError))) &&
                !tabs[currentSelectedPump?.name]?.error?.request && (
                  <div className="default-message_container">
                    <h3
                      className={
                        errorMessage &&
                        typeof errorMessage === "string" &&
                        errorMessage.startsWith(emptyFormError)
                          ? "error-message"
                          : ""
                      }
                    >
                      Введите требуемые параметры и нажмите «Подобрать» либо
                      выберите модель из списка ниже
                    </h3>
                  </div>
                )}
            </div>

            {/* Правая колонка - характеристики */}
            <div className="flex-column selection-block right-column border border-radius-l">
              {currentSelectedPump &&
                !tabs[currentSelectedPump?.name]?.error?.request && (
                  <div className="sereis-selected-option_container">
                    <SeriesSelectedOption
                      currentSelectedPump={currentSelectedPump}
                    />
                  </div>
                )}
            </div>

            {isMobile && (
              <div className="selection-bottom selection-block table-column w-100 border border-radius-l">
                {/* Таблица со списком серий */}
                <SeriesList
                  options={currentRangeList}
                  onSelectPump={(id) => openPumpHandler(id, data, true)}
                  currentSelectedPump={currentSelectedPump}
                  savedScrollTop={scrollTop}
                  onScrollChange={setScrollTop}
                  seriesId={currentPumpSeries}
                />
              </div>
            )}
          </div>

          {!isMobile && (
            <div className="selection-bottom selection-block table-column w-100 border border-radius-l">
              {/* Таблица со списком серий */}
              <SeriesList
                options={currentRangeList}
                onSelectPump={(id) => openPumpHandler(id, data, true)}
                currentSelectedPump={currentSelectedPump}
                savedScrollTop={scrollTop}
                onScrollChange={setScrollTop}
                seriesId={currentPumpSeries}
              />
            </div>
          )}

          {currentActiveTab !== "Default" && (
            <div className="back-button_container flex-row w-100 flex-end">
              <button
                onClick={() => navigateBackHandler()}
                className="back-button btn-secondary text-m"
              >
                Вернуться к предыдущему шагу
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="placeholder-block" />
      )}
    </div>
  );
};

export default PumpsSelection;
